import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { MdPlaylistAddCheckCircle, MdPlaylistAddCircle } from 'react-icons/md';

import { Concert, Years } from 'interfaces';

import { AppLocale, BAND_NAME, BAND_NAME_JP } from 'constant';

import { LanguageContext } from 'context/languageContext';

import ArrowLink from 'components/ui/ArrowLink';
import Title from 'components/ui/Title';

import {
  Wrapper,
  List,
  ListItem,
  NumberWrapper,
  TourDetailsWrapper,
  DateWrapper,
  TouNameWrapper,
  ConcertName,
  Name,
  ConcertsWrapper,
  Icon
} from './Concerts.styles';

export interface ConcertsProps {
  selectedYear: string | null;
  concerts: Years;
  isConcertsFestivals?: boolean;
}

const Concerts = ({ selectedYear, concerts, isConcertsFestivals }: ConcertsProps) => {
  const { locale } = useContext(LanguageContext);
  const intl = useIntl();

  const pageTitle = isConcertsFestivals
    ? intl.formatMessage({ id: 'concertsAndFestivals.title' })
    : intl.formatMessage({ id: 'concert.title' });

  return (
    <Wrapper>
      <Title size="big" color="secondary">
        {
          selectedYear
            ? intl.formatDate(selectedYear, {
              year: 'numeric'
            })
            : pageTitle
        }
      </Title>
      {
        Object.entries(concerts).map(([year, concertsList]: [string,  Concert[]], index) => (
          <ConcertsWrapper key={index}>
            {
              !selectedYear ? (
                <Title element="h2" size="normal" color="primary">
                  {intl.formatDate(year, {
                    year: 'numeric'
                  })}
                </Title>
              ) : null
            }
            <List>
              {
                concertsList?.map((concert, index) => {
                  const year = concert.date?.split('-')?.[0];
                  return (
                    <ListItem key={concert.id}>
                      <ConcertName>
                        <NumberWrapper>{index + 1}.</NumberWrapper>
                        <Link
                          to={
                            concert.tourId !== 'festivals-shows-special-events'
                              ? `/tours-and-shows/${concert.tourId}/${concert.id}`
                              : `/tours-and-shows/shows/festivals-shows-special-events/${year}/${concert.id}`
                          }
                        >
                          <TourDetailsWrapper>
                            <DateWrapper>
                              {intl.formatDate(new Date(concert.date), {
                                year: 'numeric', month: 'short', day: 'numeric'
                              })}
                            </DateWrapper>
                            <TouNameWrapper>
                              {concert.tourDetails
                                ? locale === AppLocale.EN ? concert.tourDetails?.englishName : concert.tourDetails?.name
                                : intl.formatMessage({ id: 'concertsAndFestivals.title' })
                              }
                            </TouNameWrapper>
                            <Icon hasSongList={concert.setListConfirmed && concert.songList.length !== 0}>
                              {
                                concert.songList.length ? (
                                  <MdPlaylistAddCheckCircle title={intl.formatMessage({ id: 'concert.setList.added' })} />
                                ) : <MdPlaylistAddCircle title={intl.formatMessage({ id: 'concert.setList.missing' })} />
                              }
                            </Icon>
                          </TourDetailsWrapper>
                          <Name>
                            {intl.formatMessage({ id: 'concert.name' }, {
                              bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
                              name: concert.name ? `${intl.formatMessage({ id: `concert.name.${concert.name}` })}${locale === AppLocale.EN ? ', ' : '、'}` : '',
                              venue: intl.formatMessage({ id: `concert.venue.${concert.venueId}` }),
                              city: intl.formatMessage({ id: `concert.city.${concert.cityId}` }),
                              country: intl.formatMessage({ id: `concert.country.${concert.countryId}` })
                            })}
                          </Name>
                        </Link>
                      </ConcertName>
                    </ListItem>
                  )
                })
              }
            </List>
            {
              selectedYear && isConcertsFestivals ? (
                <ArrowLink to={`/tours-and-shows/shows/${selectedYear}`}>
                  {intl.formatMessage({ id: 'concerts.selectedYear.seeAll' })}
                </ArrowLink>
              ) : null
            }
          </ConcertsWrapper>
        ))
      }
    </Wrapper>
  );
};

export default Concerts;
